import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import {
  DataTableColumnPreferences,
  DataTableColumnPreferencesConfig,
  getDefaultColumnPreferences,
  loadDataTableColumnPreferences,
  saveDataTableColumnPreferences,
} from "../ui-components/data-table/dataTableColumnPreferencesStorage";

export interface UseDataTableColumnPreferencesOptions extends DataTableColumnPreferencesConfig {
  instanceKey: string;
  persist?: boolean;
}

const SAVE_DEBOUNCE_MS = 300;

function applyUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === "function" ? (updater as (prev: T) => T)(prev) : updater;
}

export function useDataTableColumnPreferences({
  instanceKey,
  columnOptions,
  hiddenByDefault,
  persist = true,
}: UseDataTableColumnPreferencesOptions) {
  const config = useMemo(
    () => ({ columnOptions, hiddenByDefault }),
    [columnOptions, hiddenByDefault]
  );

  const [state, setState] = useState<DataTableColumnPreferences>(() =>
    persist
      ? loadDataTableColumnPreferences(instanceKey, config)
      : getDefaultColumnPreferences(config)
  );

  const instanceKeyRef = useRef(instanceKey);
  instanceKeyRef.current = instanceKey;

  const debouncedSave = useMemo(
    () =>
      debounce((key: string, prefs: DataTableColumnPreferences) => {
        saveDataTableColumnPreferences(key, prefs);
      }, SAVE_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSave.flush();
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  useEffect(() => {
    if (!persist) {
      return;
    }
    debouncedSave(instanceKeyRef.current, state);
  }, [persist, state, debouncedSave]);

  const onColumnVisibilityChange = useCallback(
    (
      updater:
        | Record<string, boolean>
        | ((prev: Record<string, boolean>) => Record<string, boolean>)
    ) => {
      setState((prev) => ({
        ...prev,
        visibility: applyUpdater(updater, prev.visibility),
      }));
    },
    []
  );

  const onColumnOrderChange = useCallback(
    (updater: string[] | ((prev: string[]) => string[])) => {
      setState((prev) => ({
        ...prev,
        order: applyUpdater(updater, prev.order),
      }));
    },
    []
  );

  const onColumnSizingChange = useCallback(
    (
      updater:
        | Record<string, number>
        | ((prev: Record<string, number>) => Record<string, number>)
    ) => {
      setState((prev) => ({
        ...prev,
        sizing: applyUpdater(updater, prev.sizing),
      }));
    },
    []
  );

  return {
    columnVisibility: state.visibility,
    onColumnVisibilityChange,
    columnOrder: state.order,
    onColumnOrderChange,
    columnSizing: state.sizing,
    onColumnSizingChange,
  };
}
